import "reflect-metadata";
import React from 'react';
import './index.css';
import {App} from './App';
import {store} from './app/store/store';
import {Provider} from 'react-redux';
import "./translations/config";
import * as Sentry from "@sentry/react";
import {Integrations} from "@sentry/tracing";
import {createRoot} from "react-dom/client";
import {IServerError} from "./interface/Basic/api";
import {APIErrorCodeEnum, APIStatusEnum} from "./enum/api";
import {resetState, setNeedPasswordReset} from "./features/Auth/slice";
import {resetRegister} from "./utils/resetRegister";
import {setGlobalAlert} from "./features/Alert/slice";
import {AlertType} from "./components/ui/Alert";
import {AxiosResponse} from "axios";
import {isIos} from "./utils/browser";

// чтобы сафари нам не зумил страницу при фокусе на инпут
const addMaximumScaleToMetaViewport = () => {
    const el = document.querySelector('meta[name=viewport]');
    if (el !== null) {
        let content = el.getAttribute('content') || '';
        const re = /maximum\-scale=[0-9\.]+/g;
        if (re.test(content)) {
            content = content.replace(re, 'maximum-scale=1.0');
        } else {
            content = [content, 'maximum-scale=1.0'].join(', ')
        }
        el.setAttribute('content', content);
    }
};
const disableIosTextFieldZoom = addMaximumScaleToMetaViewport;

if (isIos) {
    disableIosTextFieldZoom();
}

Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DNS,
    integrations: [new Integrations.BrowserTracing()],
    tracesSampleRate: 1.0,
    release: "rudn-passport-frontend@0.1.0",
    environment: process.env.NODE_ENV,
})

const errorExceptions = [
    {url: '/person/avatar', method: 'get'},
    {url: '/person/snils', method: 'get'},
    {url: '/person/identity-card', method: 'get'},
    {url: '/oauth2/continue', method: 'post'},
    {url: '/confirmation/verify-call-back-by-user', method: 'post'},
]
const errorUnauthorizeExceptions = [
    '/oauth2/continue',
    'auth/connect/bind',
]


export const errorHandler = (error: IServerError) => {
    if (
        error.response?.data?.error?.code === APIErrorCodeEnum.NeedResetPassword
    ) {
        store.dispatch(setNeedPasswordReset(true))
        return
    }
    if (
        error.response.status === APIStatusEnum.Unauthorized
        && error.response.config?.url
        && !errorUnauthorizeExceptions.includes(error.response.config?.url)
        && store.getState().auth.token
    ) {
        store.dispatch(resetState())
        resetRegister()
        window.location.replace('/sign-in')
        return
    }

    if (
        error
        && error.response?.data?.error?.status !== APIStatusEnum.UnprocessableEntity
        && error.response.status !== APIStatusEnum.UnprocessableEntity
    ) {


        if (!errorExceptions.find(i => i.url === error.response.config?.url
            && i.method === error.response.config?.method)) {
            store.dispatch(setGlobalAlert({
                message: error.response.data?.error?.message
                    || error.response.data?.error?.description
                    || 'Произошла ошибка, попробуйте еще раз',
                type: AlertType.Danger
            }))
        }
    }

    return Promise.reject({...error})
};

export const successHandler = (response: AxiosResponse) => {
    if (response.config?.method !== 'get' && response.config?.method !== 'delete') {
        switch (response.config?.url) {
            case '/person/avatar':
                store.dispatch(setGlobalAlert({
                    message: 'Фотография сохранена',
                    type: AlertType.Success
                }))
                break
            case '/person/snils':
                store.dispatch(setGlobalAlert({
                    message: 'Снилс сохранен',
                    type: AlertType.Success
                }))
                break
            case '/person/identity-card':
                store.dispatch(setGlobalAlert({
                    message: 'Данные удостоверения личности сохранены',
                    type: AlertType.Success
                }))
                break
            case '/person':
                store.dispatch(setGlobalAlert({
                    message: 'Персональная информация сохранена',
                    type: AlertType.Success
                }))
                break
            case '/user/phone/submit-code':
                store.dispatch(setGlobalAlert({
                    message: 'Номер телефона изменен',
                    type: AlertType.Success
                }))
                break
            case '/user/email/submit-code':
                store.dispatch(setGlobalAlert({
                    message: 'Электронная почта изменена',
                    type: AlertType.Success
                }))
                break
            case '/user/password':
                store.dispatch(setGlobalAlert({
                    message: 'Пароль изменён',
                    type: AlertType.Success
                }))
                break

            case '/user/corporate_email/submit-code ':
                store.dispatch(setGlobalAlert({
                    message: 'Корпоративная почта добавлена',
                    type: AlertType.Success
                }))
                break
        }
    }
    return response
};


const container = document.getElementById('root');
// eslint-disable-next-line  @typescript-eslint/no-non-null-assertion
const root = createRoot(container!);
root.render(<React.StrictMode>
    <Provider store={store}>
        <App/>
    </Provider>
</React.StrictMode>);
